import frTranslations from "../../locales/fr/translation.json";

/**
 * Normalize material name from translated name to referenceName
 * Uses French translation.json to do reverse lookup
 * This is a temporary solution to handle old data with translated names
 * @param {string} materialName - The material name (can be translated or referenceName)
 * @returns {string} - The referenceName or original materialName if not found
 */
export const normalizeMaterialNameFromTranslation = (materialName) => {
  if (!materialName) return materialName;

  const materialNameLower = materialName.toLowerCase().trim().replace(/_/g, "");

  // First check if it's already a referenceName by checking if it exists as a key in translations
  // and if the translation value is different from the key (meaning it's translated)
  const translationValue = frTranslations[materialNameLower];
  if (translationValue && typeof translationValue === "string") {
    // If the translation exists and is different from the key, it's likely already a referenceName
    // But we need to check if the translation matches the input (meaning input is translated)
    if (translationValue.toLowerCase().trim() !== materialNameLower) {
      // The key exists but translation is different, so materialNameLower might be a referenceName
      // Continue to check if it's a translated name
    } else {
      // Key exists and translation equals key, likely already a referenceName
      return materialName;
    }
  }

  // Do reverse lookup: find the key where the translation value matches the materialName
  for (const [key, value] of Object.entries(frTranslations)) {
    if (
      typeof value === "string" &&
      value.toLowerCase().trim().replace(/_/g, "") === materialNameLower
    ) {
      // Found a match, return the key as referenceName
      return key;
    }
  }

  // If not found in translations, might already be a referenceName or unknown material
  // Check if it's a valid referenceName by checking if it exists as a key
  if (frTranslations[materialNameLower] !== undefined) {
    return materialName;
  }

  return materialName;
};

const normalizeLookupKey = (value) =>
  value?.toLowerCase().trim().replace(/_/g, "");

const getReferenceNameFromMaterialsDescription = (lookupKey) => {
  if (!lookupKey) return null;

  try {
    const materialListStr = localStorage.getItem("materialsDescription");
    if (!materialListStr) return null;

    const materialList = JSON.parse(materialListStr);

    for (const mat of materialList) {
      if (normalizeLookupKey(mat.referenceName) === lookupKey) {
        return mat.referenceName;
      }
      if (
        mat.possibleNames?.some(
          (name) => normalizeLookupKey(name) === lookupKey,
        )
      ) {
        return mat.referenceName;
      }
    }
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.warn("Error parsing materialsDescription:", error);
    }
  }

  return null;
};

export const normalizeMaterialKey = (materialName) => {
  if (!materialName) return materialName;

  const normalizedFromTranslation =
    normalizeMaterialNameFromTranslation(materialName);
  const lookupKey = normalizeLookupKey(normalizedFromTranslation);
  const referenceName = getReferenceNameFromMaterialsDescription(lookupKey);
  const finalKey = referenceName || normalizedFromTranslation;

  return finalKey;
};

/**
 * Format material name for display by normalizing to referenceName and translating
 * Handles both translated names and referenceNames, ensuring consistent display
 * @param {string} materialName - The material name (can be translated or referenceName)
 * @param {function} t - Translation function from useTranslation hook
 * @returns {string} - The translated material name for display
 */
export const formatMaterialNameForDisplay = (materialName, t) => {
  if (!materialName) return "-";

  const referenceName = normalizeMaterialNameFromTranslation(materialName);
  const translated = t(referenceName);

  // If translation returns the same value as the key, it might not be a valid translation key
  // In that case, if the original materialName was different, return it (it was likely already translated)
  // Otherwise, return the translated value (which might be the same as referenceName if no translation exists)
  if (translated === referenceName && materialName !== referenceName) {
    return materialName;
  }

  return translated;
};
